<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { QuillEditor } from '@vueup/vue-quill';
import { onMounted, ref } from "vue";
import { useForm, Link } from '@inertiajs/vue3';
import Loading from 'vue-loading-overlay';
import Multiselect from '@vueform/multiselect';
import FlatPickr from 'vue-flatpickr-component';
import { useToast } from 'vue-toast-notification';

const props = defineProps({
    year: String,
    allHotels: Object,
    countries: Object,
    agents: Object,
    flash: Object,
    hotelData: Object,
    booking: Object,
    user: Object,
});
const $toast = useToast();
let isLoading = ref(false);
let roomtypes = ref([]);
let transfers = ref([]);
const options = props.allHotels;
const configDept = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});
const configArrv = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
    minDate: "today",
});

const paymentDeadline = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

onMounted(() => {
    const hotel = props.hotelData.find((item) => item.id == props.booking[0].hotel_id);
    roomtypes.value = hotel.rooms;
    transfers.value = hotel.hotel_transfers;

    const readonlyInput = document.querySelectorAll('input[readonly]');
    readonlyInput.forEach((item) => {
        item.style.backgroundColor = '#eceef14a';
    });
});

function handleRoomChange() {
    const roomstemparr = BookingEditform.roomsarray;
    BookingEditform.roomsarray = [];
    for (let i = 0; i < BookingEditform.no_rooms; i++) {
        BookingEditform.roomsarray.push({
            adults: [],
            children: [],
        });
    }
    //preventing the data loss when the user changes the number of rooms
    for (let i = 0; i < roomstemparr.length; i++) {
        if (i < BookingEditform.roomsarray.length) {
            BookingEditform.roomsarray[i] = roomstemparr[i];
        }
    }
}

function handleDeptDateChange() {
    configDept.value = {
        minDate: BookingEditform.arrival_date
    };
}

const handleHotelChange = (e) => {
    const hotel_id = e;
    const hotel = props.hotelData.find((item) => item.id == hotel_id);
    roomtypes.value = hotel.rooms;
    transfers.value = hotel.hotel_transfers;
    isLoading.value = true;
    setTimeout(() => {
        isLoading.value = false;
    }, 1000);
};

const BookingEditform = useForm({
    stay_type: props.booking[0].stay_type,
    meal_type: props.booking[0].meal_type,
    hotel_id: props.booking[0].hotel_id,
    room_type1: props.booking[0].booking_rooms[0]?.room_category?.id,
    nights_room1: props.booking[0].booking_rooms[0]?.nights,
    room_type2: props.booking[0].booking_rooms[1]?.room_category?.id,
    nights_room2: props.booking[0].booking_rooms[1]?.nights,
    arrv_hotel_transfer_id: props.booking[0].transfers[0]?.id,
    dept_hotel_transfer_id: props.booking[0].transfers[1]?.id,
    no_rooms: props.booking[0].no_rooms,
    agent_id: props.booking[0].agent_id,
    arrival_date: props.booking[0].arrival_date,
    departure_date: props.booking[0].departure_date,
    offer_code: props.booking[0].offer_code,
    nationalities: props.booking[0].countries[0].id,
    arrival_flight: props.booking[0].arrival_flight,
    departure_flight: props.booking[0].departure_flight,
    remarks: props.booking[0].remarks,
    bookingid: props.booking[0].id,
    resort_confirmation_number: props.booking[0].resort_confirmation_number,
    booking_payment_deadline: props.booking[0].booking_payment_deadline,
});

const BookingEditformSubmit = () => {
    BookingEditform.post('/bookings/update', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/css/vue-quill.snow.css";
@import "vue-loading-overlay/dist/vue-loading.css";
@import "@vueform/multiselect/themes/default.css";
@import 'flatpickr/dist/themes/material_orange.css';
</style>
<template>
    <AppLayout title="Edit Booking" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card mb-4">
                <h5 class="card-header">Edit Booking <span class="badge bg-label-warning">{{
                    props.booking[0].booking_no
                }}</span>
                </h5>
                <form class="card-body" @submit.prevent="BookingEditformSubmit">
                    <h6 class="text-primary">Property Information</h6>
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label for="stay_type" class="form-label"><b>Stay Type</b></label>
                            <select class="form-select" name="stay_type" v-model="BookingEditform.stay_type">
                                <option value="0">Single Stay</option>
                                <option value="1">Split Stay</option>
                            </select>
                        </div>
                        <div class=" col-md-4">
                            <label for="hotel_id" class="form-label"><b>Hotel Name</b></label>
                            <Multiselect v-model="BookingEditform.hotel_id" :options="options" :closeOnSelect="true"
                                :searchable=true :clearOnSelect=false placeholder="Select hotel ..." track-by="name"
                                label="name" @change="handleHotelChange" :required=true />

                        </div>
                        <div class="col-md-4">
                            <label for="meal_type" class="form-label"><b>Meal Type</b></label>
                            <input type="text" class="form-control" name="meal_type" placeholder="Meal Type"
                                v-model="BookingEditform.meal_type"
                                :class="{ 'is-invalid': BookingEditform.errors.meal_type }">
                            <div v-if="BookingEditform.errors.meal_type" class="invalid-feedback">
                                <div v-text="BookingEditform.errors.meal_type">
                                </div>
                            </div>
                        </div>

                        <div class="col-md-6">
                            <label for="room_type1" class="form-label"><b>Room Type</b></label>
                            <select class="form-select" name="room_type1" required="required"
                                v-model="BookingEditform.room_type1">
                                <option value="" disabled="disabled" selected="selected">Select a room type ...
                                </option>
                                <option v-for="rooms in roomtypes" :key="rooms.room_category?.id"
                                    :value="rooms.room_category?.id">
                                    {{ rooms.room_category?.name }}</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label for="nights_room1" class="form-label"><b>Nights in this room type</b></label>
                            <select class="form-select" name="nights_room1" v-model="BookingEditform.nights_room1"
                                required="required">
                                <option value="" disabled="disabled" selected="selected">Select no of nights ...
                                </option>
                                <option value="1">1 Night</option>
                                <option value="2">2 Nights</option>
                                <option value="3">3 Nights</option>
                                <option value="4">4 Nights</option>
                                <option value="5">5 Nights</option>
                                <option value="6">6 Nights</option>
                                <option value="7">7 Nights</option>
                                <option value="8">8 Nights</option>
                                <option value="9">9 Nights</option>
                                <option value="10">10 Nights</option>
                                <option value="11">11 Nights</option>
                                <option value="12">12 Nights</option>
                                <option value="13">13 Nights</option>
                                <option value="14">14 Nights</option>
                                <option value="15">15 Nights</option>
                                <option value="16">16 Nights</option>
                                <option value="17">17 Nights</option>
                                <option value="18">18 Nights</option>
                                <option value="19">19 Nights</option>
                                <option value="20">20 Nights</option>
                            </select>
                        </div>

                        <div class="col-md-6" v-if="BookingEditform.stay_type == '1'">
                            <select class="form-select" name="room_type2" v-model="BookingEditform.room_type2">
                                <option value="" disabled="disabled" selected="selected">Select a room type ...
                                </option>
                                <option v-for="rooms in roomtypes" :key="rooms.room_category?.id"
                                    :value="rooms.room_category?.id">
                                    {{ rooms.room_category?.name }}</option>
                            </select>
                        </div>
                        <div class="col-md-6" v-if="BookingEditform.stay_type == '1'">
                            <select class="form-select" name="nights_room2" v-model="BookingEditform.nights_room2">
                                <option value="" disabled="disabled" selected="selected">Select number of nights ...
                                </option>
                                <option value="1">1 Night</option>
                                <option value="2">2 Nights</option>
                                <option value="3">3 Nights</option>
                                <option value="4">4 Nights</option>
                                <option value="5">5 Nights</option>
                                <option value="6">6 Nights</option>
                                <option value="7">7 Nights</option>
                                <option value="8">8 Nights</option>
                                <option value="9">9 Nights</option>
                                <option value="10">10 Nights</option>
                                <option value="11">11 Nights</option>
                                <option value="12">12 Nights</option>
                                <option value="13">13 Nights</option>
                                <option value="14">14 Nights</option>
                                <option value="15">15 Nights</option>
                                <option value="16">16 Nights</option>
                                <option value="17">17 Nights</option>
                                <option value="18">18 Nights</option>
                                <option value="19">19 Nights</option>
                                <option value="20">20 Nights</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label for="arrv_hotel_transfer_id" class="form-label"><b>Arrival Transfer</b></label>
                            <select class="form-select" name="arrv_hotel_transfer_id"
                                v-model="BookingEditform.arrv_hotel_transfer_id" required="required">
                                <option value="" disabled="disabled" selected="selected">Select arrv transfer ...
                                </option>
                                <option v-for="transfer in transfers" :key="transfer.id" :value="transfer.id">
                                    {{ transfer.vessel }}</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label for="dept_hotel_transfer_id" class="form-label"><b>Departure Transfer</b></label>
                            <select class="form-select" name="dept_hotel_transfer_id"
                                v-model="BookingEditform.dept_hotel_transfer_id" required="required">
                                <option value="" disabled="disabled" selected="selected">Select dept transfer ...
                                </option>
                                <option v-for="transfer in transfers" :key="transfer.id" :value="transfer.id">
                                    {{ transfer.vessel }}</option>
                            </select>
                        </div>
                        <div class="col-md-3">
                            <label for="no_rooms" class="form-label"><b>Number of rooms</b></label>
                            <select class="form-select" name="no_rooms" v-model="BookingEditform.no_rooms"
                                required="required" @change="handleRoomChange">
                                <option value="" disabled="disabled" selected="selected">Select no of rooms ...
                                </option>
                                <option :value="1">1</option>
                                <option :value="2">2</option>
                                <option :value="3">3</option>
                                <option :value="4">4</option>
                                <option :value="5">5</option>
                                <option :value="6">6</option>
                                <option :value="7">7</option>
                                <option :value="8">8</option>
                                <option :value="9">9</option>
                                <option :value="10">10</option>
                            </select>
                        </div>
                        <div class="col-md-3">
                            <label for="agent_id" class="form-label"><b>Agent</b></label>
                            <select class="form-select" name="agent_id" v-model="BookingEditform.agent_id"
                                required="required">
                                <option value="" disabled="disabled" selected="selected">Type an agent name...</option>
                                <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                                    {{ agent.agency_name }}</option>
                            </select>
                        </div>
                        <div class="col-md-3">
                            <label for="agent_ref" class="form-label"><b>Resort Ref</b></label>
                            <input type="text" class="form-control" name="resort_confirmation_number"
                                v-model="BookingEditform.resort_confirmation_number" placeholder="Resort Ref">
                        </div>
                        <div class="col-md-3">
                            <label for="booking_payment_deadline" class="form-label"><b>Booking Payment
                                    Deadline</b></label>
                            <FlatPickr v-model="BookingEditform.booking_payment_deadline" :config="paymentDeadline"
                                class="form-control" placeholder="Payment Deadline" name="booking_payment_deadline"
                                @change="handleDeptDateChange"
                                :class="{ 'is-invalid': BookingEditform.errors.booking_payment_deadline }" />
                            <div v-if="BookingEditform.errors.booking_payment_deadline" class="invalid-feedback">
                                <div v-text="BookingEditform.errors.booking_payment_deadline">
                                </div>
                            </div>
                        </div>

                    </div>
                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Stay Information</h6>
                    <div class="row g-3">
                        <div class="col-md-3">
                            <label for="arrival_date" class="form-label"><b>Check in</b></label>
                            <FlatPickr v-model="BookingEditform.arrival_date" :config="configArrv" class="form-control"
                                placeholder="Check in Date" @change="handleDeptDateChange"
                                :class="{ 'is-invalid': BookingEditform.errors.arrival_date }" />
                            <div v-if="BookingEditform.errors.arrival_date" class="invalid-feedback">
                                <div v-text="BookingEditform.errors.arrival_date">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="departure_date" class="form-label"><b>Check out</b></label>
                            <FlatPickr v-model="BookingEditform.departure_date" :config="configDept" class="form-control"
                                placeholder="Check out Date" name="departure_date"
                                :class="{ 'is-invalid': BookingEditform.errors.departure_date }" />
                            <div v-if="BookingEditform.errors.departure_date" class="invalid-feedback">
                                <div v-text="BookingEditform.errors.departure_date">
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <label for="offer_code" class="form-label"><b>Offer Code</b></label>
                            <input type="text" class="form-control" name="offer_code" v-model="BookingEditform.offer_code"
                                placeholder="Offer Code">
                        </div>
                        <div class="col-md-3">
                            <label for="nationalities" class="form-label"><b>Nationalities</b></label>
                            <select class="form-select" name="nationalities" v-model="BookingEditform.nationalities"
                                required="required">
                                <option value="" disabled="disabled" selected="selected">Select a country ...</option>
                                <option v-for="country in countries" :key="country.id" :value="country.id">
                                    {{ country.name }}</option>
                            </select>
                        </div>
                        <div class="col-md-6">
                            <label for="arrival_flight" class="form-label"><b>Arrival Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Arrival Flight or Info"
                                name="arrival_flight" v-model="BookingEditform.arrival_flight" />
                        </div>
                        <div class="col-md-6">
                            <label for="departure_flight" class="form-label"><b>Departure Flight / Info</b></label>
                            <input type="text" class="form-control" placeholder="Departure Flight or Info"
                                name="departure_flight" v-model="BookingEditform.departure_flight" />
                        </div>
                    </div>

                    <hr class="my-4 mx-n4">
                    <h6 class="text-primary">Other Information</h6>
                    <div class="row g-3">
                        <div class="col-md-12">
                            <label for="description" class="form-label"><b>Remarks</b></label>
                            <QuillEditor theme="snow" placeholder="    Type extra remarks"
                                v-model:content="BookingEditform.remarks" contentType="html" toolbar="essential"
                                object="true" :searchable="true" name="remarks" />
                        </div>
                    </div>
                    <div class="modal-footer border-0 mt-3">
                        <Link type="button" class="btn btn-label-secondary" :href="'/hotels'">
                        Cancel
                        </Link>
                        <button type="submit" class="btn btn-primary me-sm-3 me-1"
                            :disabled="BookingEditform.processing">Update Booking</button>
                    </div>
                </form>
            </div>
        </div>
    </AppLayout>
</template>